import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, Paper, Grid, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider
} from '@mui/material';
import { Save, Trash2, Check, Clock, Eye, Settings, Activity, Layers } from 'lucide-react';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import { AdminPanelSkeleton, adminPanelPlaceholderSx, useCachedPanelCount } from './adminPanelContent';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';

function formatPlayTime(seconds) {
  if (!seconds) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function SaveViewModal({ open, onClose, save }) {
  if (!save) return null;

  const rows = [
    { label: 'Type', value: save.saveType === 'current_progress' ? 'Current Progress' : save.saveType === 'settings' ? 'Settings' : `Save Slot ${save.slot}` },
    { label: 'Label', value: save.label || '—' },
    { label: 'Current Chapter', value: save.currentChapter || '—' },
    { label: 'Play Time', value: save.playTime ? formatPlayTime(save.playTime) : '—' },
    { label: 'Game Time', value: save.gameTime || '—' },
    { label: 'Game Template', value: save.gameTemplate || '—' },
    { label: 'Storyflow ID', value: save.currentStoryflowId || '—' },
    { label: 'Node ID', value: save.currentNodeId || '—' },
    { label: 'Saved By', value: save.savedBy || '—' },
    { label: 'Created', value: save.createdAt ? new Date(save.createdAt).toLocaleString() : '—' },
    { label: 'Updated', value: save.updatedAt ? new Date(save.updatedAt).toLocaleString() : '—' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#e0e0e0' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Eye size={18} />
        <span>{save.label || `Slot ${save.slot}`}</span>
        <Chip
          label={save.saveType === 'current_progress' ? 'Progress' : save.saveType === 'settings' ? 'Settings' : 'Save Slot'}
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: save.saveType === 'current_progress' ? 'rgba(96,165,250,0.15)' : save.saveType === 'settings' ? 'rgba(168,85,247,0.15)' : 'rgba(74,222,128,0.15)',
            color: save.saveType === 'current_progress' ? '#60a5fa' : save.saveType === 'settings' ? '#a855f7' : '#4ade80',
          }}
        />
      </DialogTitle>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <DialogContent sx={{ pt: 2 }}>
        {rows.map((r) => (
          <Box key={r.label} display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120 }}>{r.label}</Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', textAlign: 'right', wordBreak: 'break-all' }}>{r.value}</Typography>
          </Box>
        ))}
        {save.state && Object.keys(save.state).length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>State (raw)</Typography>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 1.5, maxHeight: 200, overflow: 'auto', fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
              {JSON.stringify(save.state, null, 2)}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SaveCard({ save, onView, onDelete, typeIcon, typeColor }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', '&:hover': { borderColor: typeColor, transition: 'border-color 0.2s' } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Box display="flex" alignItems="center" gap={0.75}>
          {typeIcon}
          <Typography variant="subtitle2" fontWeight={600}>
            {save.label || `Slot ${save.slot}`}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.25}>
          <IconButton size="small" onClick={() => onView(save)} title="View details" sx={{ color: 'text.secondary', p: 0.35, borderRadius: '6px' }}>
            <Eye size={14} strokeWidth={2} />
          </IconButton>
          <ConfirmDeleteButton onConfirm={() => onDelete(save.id)} />
        </Box>
      </Box>
      {save.currentChapter && (
        <Typography variant="caption" sx={{ color: 'primary.light', display: 'block', mb: 0.25 }}>
          {save.currentChapter}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
        <Clock size={12} style={{ color: '#71717a' }} />
        <Typography variant="caption" color="text.secondary">
          {save.updatedAt ? new Date(save.updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {save.playTime > 0 && (
          <Typography variant="caption" color="text.disabled">
            Play: {formatPlayTime(save.playTime)}
          </Typography>
        )}
        {save.gameTime && (
          <Typography variant="caption" color="text.disabled">
            Game: {save.gameTime}
          </Typography>
        )}
        {!save.playTime && !save.gameTime && (
          <Typography variant="caption" color="text.disabled">
            Slot {save.slot} · {save.gameTemplate}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default function SavesPanel({ getToken }) {
  const { activeTeamId } = useTeam();
  const [saves, setSaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [viewSave, setViewSave] = useState(null);
  const [cachedCount, setCachedCount] = useCachedPanelCount(activeTeamId ? `saves:${activeTeamId}` : null, 6);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  const load = useCallback(async () => {
    if (!activeTeamId) return;
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const res = await fetch(
        `${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(activeTeamId)}`,
        { headers: h }
      );
      if (!res.ok) throw new Error('Failed to load saves');
      const data = await res.json();
      const list = data.saves || [];
      setSaves(list);
      setCachedCount(list.length);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId, authHeaders, setCachedCount]);

  useEffect(() => { load(); }, [load]);

  const deleteSave = async (saveId) => {
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/game-saves/${encodeURIComponent(saveId)}`, {
        method: 'DELETE',
        headers: h
      });
      if (!res.ok) throw new Error('Failed to delete');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteAllSaves = async () => {
    if (!activeTeamId) return;
    try {
      const h = await authHeaders();
      const res = await fetch(
        `${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(activeTeamId)}`,
        { method: 'DELETE', headers: h }
      );
      if (!res.ok) throw new Error('Failed to delete all saves');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const progressSaves = saves.filter((s) => s.saveType === 'current_progress');
  const settingsSaves = saves.filter((s) => s.saveType === 'settings');
  const slotSaves = saves.filter((s) => !s.saveType || s.saveType === 'save_slot');

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ color: 'secondary.light', display: 'flex', alignItems: 'center' }}>
            <Save size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Game Saves
          </Typography>
        </Box>
        <Button
          variant={confirmDeleteAll ? 'contained' : 'outlined'}
          color="error"
          size="small"
          startIcon={confirmDeleteAll ? <Check size={16} /> : <Trash2 size={16} />}
          onClick={() => {
            if (confirmDeleteAll) {
              setConfirmDeleteAll(false);
              deleteAllSaves();
            } else {
              setConfirmDeleteAll(true);
              setTimeout(() => setConfirmDeleteAll(false), 2500);
            }
          }}
          disabled={loading || saves.length === 0}
        >
          {confirmDeleteAll ? 'Confirm delete all' : 'Delete all'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <AdminPanelSkeleton variant="grid" count={cachedCount} />
      ) : saves.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">
            No save files yet. Play the game to create saves.
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Current Progress */}
          {progressSaves.length > 0 && (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Activity size={16} style={{ color: '#60a5fa' }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#60a5fa' }}>
                  Current Progress
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {progressSaves.map((save) => (
                  <Grid item xs={12} sm={6} md={4} key={save.id}>
                    <SaveCard
                      save={save}
                      onView={setViewSave}
                      onDelete={deleteSave}
                      typeIcon={<Activity size={13} style={{ color: '#60a5fa' }} />}
                      typeColor="#60a5fa"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Settings */}
          {settingsSaves.length > 0 && (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Settings size={16} style={{ color: '#a855f7' }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#a855f7' }}>
                  Settings
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {settingsSaves.map((save) => (
                  <Grid item xs={12} sm={6} md={4} key={save.id}>
                    <SaveCard
                      save={save}
                      onView={setViewSave}
                      onDelete={deleteSave}
                      typeIcon={<Settings size={13} style={{ color: '#a855f7' }} />}
                      typeColor="#a855f7"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Save Slots */}
          {slotSaves.length > 0 && (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Layers size={16} style={{ color: '#4ade80' }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#4ade80' }}>
                  Save Slots
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {slotSaves.map((save) => (
                  <Grid item xs={12} sm={6} md={4} key={save.id}>
                    <SaveCard
                      save={save}
                      onView={setViewSave}
                      onDelete={deleteSave}
                      typeIcon={<Save size={13} style={{ color: '#4ade80' }} />}
                      typeColor="#4ade80"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      <SaveViewModal open={!!viewSave} onClose={() => setViewSave(null)} save={viewSave} />
    </Box>
  );
}
